// Per-element-type schema of animatable sub-fields. Drives extractAnimations
// so every supported sub-field shows up on the timeline even before any
// animation is set, and the AnimationPanel knows which preset filter applies.
//
// Field kinds:
//   scalar       text leaf — text presets (Type Writer / Wavy) available
//   scalar-box   text-shaped leaf rendered as a box — no text presets
//   text-input   array item that IS a TextInput leaf
//   item-box     array item that is a composite box, inner fields plain
//   container    array/column item with its own anim AND inner schema

export const ATOMIC_ANIMATION_SCHEMA = {
  eyebrow: { textOnly: true, uiType: "text" },
  heading: { textOnly: true, uiType: "text" },
  footer: { textOnly: true, uiType: "text" },
  badge: { textOnly: true, uiType: "box" },

  divider: { scalars: ["label"] },
  "cta-button": { scalars: ["subtext"], scalarBoxes: ["label"] },

  hero: { scalars: ["eyebrow", "heading", "body"], scalarBoxes: ["badge"] },
  section: { scalars: ["sectionNumber", "heading", "body"] },
  "icon-block": { scalars: ["eyebrow", "title", "body"] },
  quote: {
    scalars: ["quote", "attribution", "role"],
    labels: { attribution: "Author Name" },
  },

  "bullet-list": {
    scalars: ["title"],
    arrays: {
      items: { kind: "text-input", labelFrom: "text" },
    },
  },

  "card-grid": {
    scalars: ["title"],
    arrays: {
      items: { kind: "item-box", labelFrom: "title" },
    },
  },
  timeline: {
    scalars: ["title"],
    arrays: {
      steps: { kind: "item-box", labelFrom: "title" },
    },
  },
  "stat-group": {
    scalars: ["title"],
    arrays: {
      stats: { kind: "item-box", labelFrom: "value" },
    },
  },

  // Two-column accepts modern element.columns[] (dynamic) and legacy
  // element.left / element.right; renderer prefers columns[] when present
  // and extractAnimations mirrors that precedence.
  "two-column": {
    scalars: ["eyebrow", "heading"],
    arrays: {
      columns: {
        kind: "container",
        labelFrom: "title",
        scalars: ["title", "body"],
        arrays: { bullets: { kind: "text-input", labelFrom: "text" } },
      },
    },
    columns: {
      left: {
        kind: "container",
        labelFrom: "title",
        scalars: ["title", "body"],
        arrays: { bullets: { kind: "text-input", labelFrom: "text" } },
      },
      right: {
        kind: "container",
        labelFrom: "title",
        scalars: ["title", "body"],
        arrays: { bullets: { kind: "text-input", labelFrom: "text" } },
      },
    },
  },

  comparison: {
    scalars: ["eyebrow", "heading"],
    columns: {
      left: { kind: "item-box", labelFrom: "title" },
      right: { kind: "item-box", labelFrom: "title" },
    },
  },
};

export function getElementSchema(type) {
  if (!type || typeof type !== "string") return null;
  return ATOMIC_ANIMATION_SCHEMA[type] || null;
}

export function getAtomicElementUiType(type) {
  const schema = getElementSchema(type);
  if (!schema) return "box";
  if (schema.uiType === "text") return "text";
  return "box";
}

// Returns true when the leaf at `path` is a text leaf, so AnimationPanel
// can expose Type Writer / Wavy Text only for those.
export function isTextLeafPath(elementType, path) {
  const schema = getElementSchema(elementType);
  if (!schema) {
    const last = (path || "").split(".").filter(Boolean).pop();
    if (!last) return false;
    return !/^\d+$/.test(last);
  }
  const parts = (path || "").split(".").filter(Boolean);
  if (parts.length === 0) return false;

  let node = schema;
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    if (!node) return false;

    if (/^\d+$/.test(seg)) {
      if (node.kind === "text-input") return true;
      if (node.kind === "item-box") return false;
      if (node.kind === "container") continue;
      return false;
    }

    if (node.scalars && node.scalars.includes(seg)) {
      return i === parts.length - 1;
    }
    if (node.scalarBoxes && node.scalarBoxes.includes(seg)) {
      return false;
    }

    if (node.arrays && node.arrays[seg]) {
      node = node.arrays[seg];
      continue;
    }
    if (node.columns && node.columns[seg]) {
      node = node.columns[seg];
      continue;
    }

    return true;
  }

  return false;
}

export default ATOMIC_ANIMATION_SCHEMA;
